import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  utils,
  paths,
  t,
  styles,
} from './-global.min.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total) {
      return
    }

    this._current =
      Math.round(
        this.$container?.value?.scrollLeft / this.items[0].clientWidth
      ) + 1
    this._current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled')

    this._current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled')
  }

  move(event) {
    event.preventDefault()
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)}>
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
        >
          ${t('accessibility.previous_slide', 'Previous')}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
        >
          ${t('accessibility.next_slide', 'Next')}
        </button>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: hidden;
        overflow-y: visible;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scroll-padding-left: 1rem;
        -webkit-overflow-scrolling: touch;
      }

      :host([active='true']) ::slotted(*) {
        width: var(--hh-cell-width) !important;
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        top: -4.5rem;
        background: var(--hh-button-background);
      }

      :host([active='true']:not([hide-buttons])) .buttons {
        display: flex;
      }

      .button {
        width: var(--hh-button-width, 48px);
        height: var(--hh-button-height, 48px);
        cursor: pointer;
        border: none;
        background: var(--hh-button-background);
        font-size: 0;
        line-height: 0;
        color: var(--hh-color-text);
        position: relative;
      }

      .button::before {
        -webkit-font-smoothing: antialiased;
        font-family: system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif,
          Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
        font-size: 1.625rem;
        font-weight: 700;
        height: 3rem;
        left: 0;
        line-height: 2.5rem;
        margin-bottom: -0.60001rem;
        padding-top: 0.60001rem;
        position: absolute;
        top: 0;
        width: 1.5rem;
      }

      .button--prev::before {
        content: '';
        /* padding-left: 1.5rem; */
        background-image: url(https://cdn.shopify.com/s/files/1/2462/9621/files/icon-arrow-left.png?v=1741183394);
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
      }

      .button--next::before {
        content: '';
        /* padding-left: 1.5rem; */
        background-image: url(https://cdn.shopify.com/s/files/1/2462/9621/files/icon-arrow-right.png?v=1741183396);
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
